import {
  MAX_ADJUNTO_BYTES,
  RESPUESTAS_ADMIN_EMAILS,
  ESTADOS_SIN_SLA,
  SLA_AVISO_RATIO,
  SLA_AVISO_MS,
  BUZON_SOPORTE,
} from './constants';

// ¿El email logueado puede gestionar respuestas guardadas? Normaliza para
// comparar contra RESPUESTAS_ADMIN_EMAILS (el backend valida la lista real).
export const puedeGestionarRespuestas = (email) => {
  const norm = (email || '').trim().toLowerCase();
  return norm !== '' && RESPUESTAS_ADMIN_EMAILS.includes(norm);
};

// El agente asignado a un ticket se guarda por id (lo que envia el backend).
// Para pintarlo resolvemos id -> nombre con la lista de usuarios cargada.
// Fallback: si no se encuentra (p.ej. datos seed antiguos guardados por nombre),
// se devuelve el valor tal cual para no perder informacion.
export const nombreUsuario = (usuarios, agente) => {
  if (agente === null || agente === undefined || agente === '') return null;
  const usuario = (usuarios || []).find((u) => String(u.id) === String(agente));
  return usuario ? usuario.nombre : agente;
};

// Lee un File como base64 SIN el prefijo "data:<mime>;base64," (el backend
// espera solo el contenido). Devuelve una promesa con la cadena base64.
export const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const coma = result.indexOf(',');
      resolve(coma >= 0 ? result.slice(coma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

// Valida el tamano de un fichero contra el limite de adjuntos.
export const adjuntoDentroDeLimite = (file) => Boolean(file) && file.size <= MAX_ADJUNTO_BYTES;

// Tamano legible (B, KB, MB) a partir de bytes.
export const tamanoLegible = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// Tiempo relativo en castellano a partir de una fecha ISO del backend.
// Sustituye los strings "edad"/"tiempo" hardcodeados del mockup.
export const tiempoRelativo = (fechaISO) => {
  if (!fechaISO) return '';
  const fecha = new Date(fechaISO);
  if (Number.isNaN(fecha.getTime())) return '';
  const segundos = Math.round((Date.now() - fecha.getTime()) / 1000);
  if (segundos < 60) return 'ahora';
  const minutos = Math.round(segundos / 60);
  if (minutos < 60) return `hace ${minutos} min`;
  const horas = Math.round(minutos / 60);
  if (horas < 24) return `hace ${horas} h`;
  const dias = Math.round(horas / 24);
  if (dias < 30) return `hace ${dias} d`;
  const meses = Math.round(dias / 30);
  if (meses < 12) return `hace ${meses} meses`;
  return `hace ${Math.round(meses / 12)} años`;
};

// Estado de SLA de un ticket a partir de vence_sla_en (fecha limite que calcula
// el backend). Solo destaca lo accionable; devuelve null cuando no aplica:
//  - el ticket esta resuelto/cerrado (ESTADOS_SIN_SLA) o no trae fecha limite,
//  - o esta dentro de plazo con margen suficiente.
// Cuando aplica devuelve { nivel, label }:
//  - 'vencido'   -> ya paso la fecha limite.
//  - 'porvencer' -> queda <=25% de la ventana de SLA (o <4h si no hay creado_en).
export const slaEstado = (ticket) => {
  if (!ticket || !ticket.vence_sla_en) return null;
  if (ESTADOS_SIN_SLA.includes(Number(ticket.estado))) return null;

  const vence = new Date(ticket.vence_sla_en);
  if (Number.isNaN(vence.getTime())) return null;

  const restante = vence.getTime() - Date.now();
  if (restante <= 0) return { nivel: 'vencido', label: 'Vencido' };

  const creado = ticket.creado_en ? new Date(ticket.creado_en) : null;
  const ventana =
    creado && !Number.isNaN(creado.getTime()) ? vence.getTime() - creado.getTime() : 0;
  const umbral = ventana > 0 ? ventana * SLA_AVISO_RATIO : SLA_AVISO_MS;

  if (restante <= umbral) return { nivel: 'porvencer', label: 'Por vencer' };
  return null;
};

// Validacion ligera de email para el campo CC (el backend valida de verdad).
const EMAIL_RE = /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]+$/;
export const emailValido = (email) => EMAIL_RE.test(String(email || '').trim());

// ¿Se puede ofrecer este email como CC del ticket? No si es el buzon de soporte,
// el propio solicitante (ya recibe las respuestas) o si ya esta en copia.
export const esCandidatoCc = (email, { solicitante, cc = [] } = {}) => {
  const e = String(email || '')
    .trim()
    .toLowerCase();
  if (!emailValido(e)) return false;
  if (e === BUZON_SOPORTE) return false;
  if (
    e ===
    String(solicitante || '')
      .trim()
      .toLowerCase()
  )
    return false;
  return !cc.some((c) => String(c).toLowerCase() === e);
};
